import {
  ArrowsInSimple,
  CircleNotch,
  DownloadSimple,
  FileCsv,
  FileImage,
  FilePdf,
} from "@phosphor-icons/react";
import * as Accordion from "@radix-ui/react-accordion";
import { Button, Dialog, Flex, Text, Tooltip } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";

import { collectSectionIds, getBlockKey } from "./canvas/blockKey";
import { CanvasBlockRenderer } from "./canvas/CanvasBlockRenderer";
import { groupBlocksBySections } from "./canvas/groupBlocks";
import { SandboxedRenderer } from "./canvas/SandboxedRenderer";
import { type CanvasContent } from "./canvas/types";

import { useAnalytics } from "../../../utils/analytics";
import { useSectionCollapse } from "../hooks/useSectionCollapse";

interface CanvasPaneProps {
  canvas: CanvasContent & { changedSectionIds?: string[] };
  onDismiss: () => void;
  skillName?: string;
  chatId?: string;
  isLoading?: boolean;
  onSectionClick?: (sectionId: string, title: string) => void;
}

const OUTPUT_TYPE_LABELS: Record<string, string> = {
  risk_assessment: "Risk Assessment",
  brief_document: "Creative Brief",
  comparison: "Comparison",
  report: "Report",
  visualization: "Visualization",
  custom: "Output",
};

const EXPORT_ICONS: Record<string, React.ReactNode> = {
  pdf: <FilePdf size={14} />,
  csv: <FileCsv size={14} />,
  png: <FileImage size={14} />,
};

export function CanvasPane({
  canvas,
  onDismiss,
  skillName,
  chatId,
  isLoading,
  onSectionClick,
}: CanvasPaneProps) {
  const analytics = useAnalytics("ask_puntt_canvas");
  const exportFormats = canvas.exportFormats ?? [];
  const [comingSoonLabel, setComingSoonLabel] = useState<string | null>(null);

  const handleBadgeAction = (action: string, label: string) => {
    analytics.capture("ask_puntt_agent_action_enable_clicked", {
      action,
      label,
    });
    setComingSoonLabel(label);
  };

  // Track previous canvas to detect creation vs. update
  const prevCanvasRef = useRef<
    (CanvasContent & { changedSectionIds?: string[] }) | null
  >(null);

  // Highlighted section IDs from the most recent update
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isCreation =
    prevCanvasRef.current === null ||
    prevCanvasRef.current.outputType !== canvas.outputType;

  const groupedBlocks = useMemo(
    () => groupBlocksBySections(canvas.blocks),
    [canvas.blocks],
  );
  const sectionIds = useMemo(
    () => collectSectionIds(groupedBlocks),
    [groupedBlocks],
  );
  const canvasKey = `${chatId ?? "no-chat"}:${canvas.outputType}`;
  const collapse = useSectionCollapse(canvasKey, sectionIds);
  const { forceOpen } = collapse;

  useEffect(() => {
    const incoming = new Set<string>(canvas.changedSectionIds ?? []);
    if (incoming.size > 0 && !isCreation) {
      incoming.forEach((id) => forceOpen(id));
      setHighlightedIds(
        (prev) => new Set(Array.from(prev).concat(Array.from(incoming))),
      );
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(
        () => setHighlightedIds(new Set()),
        2000,
      );
    }
    prevCanvasRef.current = canvas;
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [canvas, isCreation, forceOpen]);

  const label =
    OUTPUT_TYPE_LABELS[canvas.outputType] ?? OUTPUT_TYPE_LABELS.custom;

  const handleExport = async (format: string) => {
    analytics.capture("canvas_exported", {
      output_type: canvas.outputType,
      skill_name: skillName,
      export_format: format,
      chat_id: chatId,
    });

    if (format === "csv") {
      // Serialize table blocks to CSV
      const tables = canvas.blocks.filter(
        (b) => (b as { type: string }).type === "table",
      );
      if (tables.length > 0) {
        const table = tables[0] as {
          headers: string[];
          rows: string[][];
        };
        const csvContent = [
          table.headers.join(","),
          ...table.rows.map((row) =>
            row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","),
          ),
        ].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${label.toLowerCase().replace(/\s+/g, "_")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    }

    if (format === "png") {
      try {
        // html2canvas is an optional dependency — use opaque import to
        // prevent Rollup from failing the build when it's not installed.
        const pkg = "html2canvas";
        const mod = await import(/* @vite-ignore */ pkg);
        const html2canvas = mod.default;
        const el = document.getElementById("canvas-content");
        if (el) {
          const c = await html2canvas(el);
          const url = c.toDataURL("image/png");
          const a = document.createElement("a");
          a.href = url;
          a.download = `${label.toLowerCase().replace(/\s+/g, "_")}.png`;
          a.click();
        }
      } catch {
        console.error("PNG export failed — install html2canvas");
      }
    }

    if (format === "pdf") {
      try {
        // html2pdf.js is an optional dependency — use opaque import to
        // prevent Rollup from failing the build when it's not installed.
        const pkg = "html2pdf.js";
        const mod = await import(/* @vite-ignore */ pkg);
        const html2pdf = mod.default;
        const el = document.getElementById("canvas-content");
        if (el) {
          html2pdf()
            .set({
              margin: 10,
              filename: `${label.toLowerCase().replace(/\s+/g, "_")}.pdf`,
            })
            .from(el)
            .save();
        }
      } catch {
        console.error("PDF export failed — install html2pdf.js");
      }
    }
  };

  return (
    <div
      className="flex h-full flex-col border-l border-puntt-accent-6/20 bg-puntt-neutral-gray-1"
      data-testid="canvas-pane"
    >
      {/* Header */}
      <Flex
        align="center"
        justify="between"
        className="shrink-0 border-b border-puntt-neutral-gray-6 bg-base-white px-5 py-4"
      >
        <Flex align="center" gap="2">
          <div className="size-2.5 rounded-full bg-puntt-accent-9 ring-2 ring-puntt-accent-4" />
          <Text size="3" weight="bold" className="text-puntt-neutral-gray-12">
            {label}
          </Text>
          {skillName && (
            <Text size="1" className="text-puntt-neutral-gray-9">
              via {skillName}
            </Text>
          )}
        </Flex>
        <Flex
          align="center"
          gap="1"
          className="rounded-lg bg-puntt-neutral-gray-2 px-2 py-1"
        >
          {sectionIds.length > 1 && (
            <>
              <Button
                data-testid="canvas-pane-expand-all"
                size="1"
                variant="ghost"
                onClick={collapse.openAll}
                className="cursor-pointer"
              >
                <Text size="1">Expand all</Text>
              </Button>
              <Button
                data-testid="canvas-pane-collapse-all"
                size="1"
                variant="ghost"
                onClick={collapse.closeAll}
                className="cursor-pointer"
              >
                <Text size="1">Collapse all</Text>
              </Button>
            </>
          )}
          {exportFormats.map((format) => (
            <Button
              data-testid="canvas-pane-button-204"
              key={format}
              size="1"
              variant="ghost"
              onClick={() => handleExport(format)}
              className="cursor-pointer"
            >
              {EXPORT_ICONS[format] ?? <DownloadSimple size={14} />}
              <Text size="1">{format.toUpperCase()}</Text>
            </Button>
          ))}
          <Tooltip content="Collapse canvas">
            <Button
              data-testid="canvas-pane-button-216"
              size="1"
              variant="ghost"
              onClick={onDismiss}
              className="cursor-pointer"
              aria-label="Collapse canvas"
            >
              <ArrowsInSimple size={14} />
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {/* Content */}
      <div
        id="canvas-content"
        className="relative flex-1 overflow-y-auto p-6"
        data-testid="canvas-content"
      >
        {isLoading && canvas.blocks.length > 0 && (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-center bg-puntt-neutral-gray-1/90 px-4 py-3"
            data-testid="canvas-staging-indicator"
            aria-live="polite"
          >
            <CircleNotch
              size={14}
              className="mr-2 animate-spin text-puntt-accent-9"
              aria-hidden="true"
            />
            <Text size="2" className="text-puntt-neutral-gray-11">
              Generating updated canvas…
            </Text>
          </div>
        )}
        {canvas.rawHtml ? (
          <SandboxedRenderer html={canvas.rawHtml} />
        ) : (
          <Accordion.Root
            type="multiple"
            value={collapse.value}
            onValueChange={collapse.setValue}
            className="space-y-4"
          >
            {groupedBlocks.map((block, idx) => {
              const isHighlighted =
                block.type === "section" && highlightedIds.has(block.id);
              return (
                <div
                  key={getBlockKey(block, idx)}
                  className={cx(
                    isCreation && "animate-block-fade-up",
                    isHighlighted &&
                      "-mx-2 animate-section-highlight rounded-md px-2 transition-all",
                  )}
                  style={
                    isCreation ? { animationDelay: `${idx * 50}ms` } : undefined
                  }
                >
                  <CanvasBlockRenderer
                    block={block}
                    onSectionClick={onSectionClick}
                    onBadgeAction={handleBadgeAction}
                  />
                </div>
              );
            })}
            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <CircleNotch
                  size={16}
                  className="animate-spin text-puntt-neutral-gray-7"
                />
              </div>
            )}
          </Accordion.Root>
        )}
      </div>

      <Dialog.Root
        open={comingSoonLabel != null}
        onOpenChange={(open) => {
          if (!open) setComingSoonLabel(null);
        }}
      >
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Coming Soon</Dialog.Title>
          <Dialog.Description>
            <strong>{comingSoonLabel}</strong> is not available yet. We&apos;re
            working on automated agent actions and will notify you when this
            feature is ready.
          </Dialog.Description>
          <Flex mt="3" justify="end">
            <Button
              data-testid="canvas-pane-button-288"
              variant="soft"
              onClick={() => setComingSoonLabel(null)}
            >
              Got it
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
